import { apiClient } from '../config/api';

// Types
export interface TrainingCourse {
  id: string;
  title: string;
  description: string;
  category?: string;
  difficultyLevel: 'BEGINNER' | 'INTERMEDIATE' | 'ADVANCED';
  estimatedDuration: number;
  learningObjectives: string[];
  thumbnailUrl?: string;
  videoUrl?: string;
  videoStoragePath?: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  isRequired: boolean;
  isRecommended: boolean;
  targetRoles: string[];
  passingScore: number;
  certificateTemplate?: string;
  version: number;
  publishedAt?: string;
  archivedAt?: string;
  enrollmentCount?: number;
  certificateCount?: number;
  quizQuestionCount?: number;
  completionRate?: number;
  quizQuestions?: QuizQuestion[];
  createdAt: string;
  updatedAt: string;
}

export interface QuizQuestion {
  id: string;
  courseId: string;
  question: string;
  questionType: 'MULTIPLE_CHOICE' | 'TRUE_FALSE' | 'MULTIPLE_SELECT';
  options: any;
  correctAnswer: any;
  explanation?: string;
  points: number;
  orderIndex: number;
}

export interface CourseAnalytics {
  courseId: string;
  courseName: string;
  totalEnrollments: number;
  completedCount: number;
  completionRate: number;
  certificatesIssued: number;
  averageQuizScore: number;
  quizPassRate: number;
  enrollmentTrend: any[];
}

export interface Certificate {
  id: string;
  userId: string;
  courseId: string;
  certificateNumber: string;
  courseName: string;
  completionDate: string;
  expiryDate?: string;
  isRevoked: boolean;
  revokedAt?: string;
  revokedReason?: string;
  user?: {
    firstName?: string;
    lastName?: string;
    email: string;
  };
}

// Admin Training Management Service
class TrainingManagementService {
  // Course Management
  async getCourses(params?: {
    status?: string;
    category?: string;
    difficultyLevel?: string;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<{
    courses: TrainingCourse[];
    pagination: any;
  }> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const endpoint = `/admin/training/courses${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get<{
      courses: TrainingCourse[];
      pagination: any;
    }>(endpoint);
    
    return response.data;
  }

  async getCourse(courseId: string): Promise<TrainingCourse> {
    const response = await apiClient.get<TrainingCourse>(`/admin/training/courses/${courseId}`);
    return response.data;
  }

  async createCourse(data: Partial<TrainingCourse>): Promise<TrainingCourse> {
    const response = await apiClient.post<TrainingCourse>('/admin/training/courses', data);
    return response.data;
  }

  async updateCourse(courseId: string, data: Partial<TrainingCourse>): Promise<TrainingCourse> {
    const response = await apiClient.put<TrainingCourse>(`/admin/training/courses/${courseId}`, data);
    return response.data;
  }

  async publishCourse(courseId: string): Promise<TrainingCourse> {
    const response = await apiClient.post<TrainingCourse>(`/admin/training/courses/${courseId}/publish`);
    return response.data;
  }

  async archiveCourse(courseId: string): Promise<TrainingCourse> {
    const response = await apiClient.post<TrainingCourse>(`/admin/training/courses/${courseId}/archive`);
    return response.data;
  }

  async duplicateCourse(courseId: string, newTitle?: string): Promise<TrainingCourse> {
    const response = await apiClient.post<TrainingCourse>(
      `/admin/training/courses/${courseId}/duplicate`,
      { newTitle }
    );
    return response.data;
  }

  async deleteCourse(courseId: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(
      `/admin/training/courses/${courseId}`
    );
    return response.data;
  }

  // Quiz Management
  async addQuizQuestion(courseId: string, data: Partial<QuizQuestion>): Promise<QuizQuestion> {
    const response = await apiClient.post<QuizQuestion>(
      `/admin/training/courses/${courseId}/quiz/questions`,
      data
    );
    return response.data;
  }

  async updateQuizQuestion(questionId: string, data: Partial<QuizQuestion>): Promise<QuizQuestion> {
    const response = await apiClient.put<QuizQuestion>(
      `/admin/training/quiz/questions/${questionId}`,
      data
    );
    return response.data;
  }

  async deleteQuizQuestion(questionId: string): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(
      `/admin/training/quiz/questions/${questionId}`
    );
    return response.data;
  }

  // Analytics
  async getCourseAnalytics(courseId: string): Promise<CourseAnalytics> {
    const response = await apiClient.get<CourseAnalytics>(
      `/admin/training/courses/${courseId}/analytics`
    );
    return response.data;
  }

  async getCourseLearners(courseId: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<{
    learners: any[];
    pagination: any;
  }> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const endpoint = `/admin/training/courses/${courseId}/learners${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get<{
      learners: any[];
      pagination: any;
    }>(endpoint);
    
    return response.data;
  }

  // Certificate Management
  async revokeCertificate(certificateId: string, reason: string): Promise<Certificate> {
    const response = await apiClient.post<Certificate>(
      `/admin/training/certificates/${certificateId}/revoke`,
      { reason }
    );
    return response.data;
  }

  async getCertificates(params?: {
    userId?: string;
    courseId?: string;
    isRevoked?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    certificates: Certificate[];
    pagination: any;
  }> {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const endpoint = `/certificates${queryParams.toString() ? `?${queryParams}` : ''}`;
    const response = await apiClient.get<{
      certificates: Certificate[];
      pagination: any;
    }>(endpoint);
    
    return response.data;
  }
}

export const trainingManagementService = new TrainingManagementService();

